/**
 * Inspector layout - the FP-scoped shell around every /inspect/:fp_code
 * route: auth + FP-access guards, sticky brand bar + nav chrome, the
 * sync status bar, mobile nav drawer, and the InspectorProvider-wrapped
 * outlet.
 *
 * Concern modules (exception-ui-decomposition plan):
 *   - `useFranchiseData`         - FP fetch + abort/dedup + not-found state
 *   - `useAppChromeCollapse`     - scroll collapse + `--app-chrome-height`
 *   - `useAuthRedirect` / `useSmartAuthLoading` - login redirect + spinner delay
 *   - `InspectorBrandBar`        - sticky teal header chrome
 *   - `InspectorMobileNavDrawer` - mobile nav + bottom user section
 *   - `SyncStatusBarWrapper`     - sync bar visibility/dismiss behaviour
 *
 * The desktop account cluster (user menu) stays here - it owns the menu
 * anchor state and the logout/admin handlers both surfaces share.
 */
import React, { useState, useEffect } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Box,
  Container,
  Typography,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material'
import {
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminIcon,
  Warning as WarningIcon,
  SwapHoriz as SwitchIcon
} from '@mui/icons-material'

import {
  APP_HEADER_HEIGHT,
  APP_NAV_HEIGHT,
} from '../components/shared/DataViewTable/stickyOffsets'
import InspectorNavigation from '../components/InspectorNavigation'
import { buildInspectorNavigationItems, filterNavItemsForUser } from '../config/inspectorNavigation'
import NotificationBell from '../components/notifications/NotificationBell'
import InspectorAlertsMount from '../components/inspector/AlertStack/InspectorAlertsMount'
import { useAuth } from '../contexts/AuthContext'
import { InspectorProvider } from '../contexts/InspectorContext'
import { getProfileImageUrl, getUserInitials } from '../utils/profileImage'
import InspectorBreadcrumbs from '../components/inspector/InspectorBreadcrumbs'
import VersionDisplay from '../components/common/VersionDisplay'
import DevEnvironmentBanner from '../components/DevEnvironmentBanner'
import { FeedbackUIProvider } from '../components/feedback/FeedbackUIContext'
import HelpFeedbackDrawer from '../components/feedback/HelpFeedbackDrawer'
import HelpFeedbackFab from '../components/feedback/HelpFeedbackFab'
import { useAppInfo } from '../hooks/useVersion'
import NotFound from '../pages/NotFound'
import FranchiseAccessDenied from '../components/inspector/FranchiseAccessDenied'
import { hasAccessToFp } from '../services/auth'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useInspectorRef } from '../hooks/useInspectorRef'
import { buildInspectorRef } from '../utils/urls'
import { useAppChromeCollapse } from '../hooks/useAppChromeCollapse'
import { useAuthRedirect, useSmartAuthLoading } from '../hooks/useAuthRedirect'
import InspectorBrandBar from '../components/inspector/layout/InspectorBrandBar'
import InspectorMobileNavDrawer from '../components/inspector/layout/InspectorMobileNavDrawer'
import SyncStatusBarWrapper from '../components/inspector/layout/SyncStatusBarWrapper'
import { getRoleDisplayName, getRoleHexColor } from '../utils/roleDisplay'

interface InspectorLayoutProps {
  franchiseName?: string
  fpCode?: string
}

const InspectorLayout: React.FC<InspectorLayoutProps> = () => {
  // `fp_code` is the raw segment (`<code>-<letter>`); `fpCode` is the bare code.
  // Data/access/fetch key on the bare code; the URL is canonicalised below.
  const { ref: fp_code, fpCode } = useInspectorRef()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  useAppInfo() // Keep hook for other potential uses
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const [imageError, setImageError] = useState(false)
  const [syncRefreshKey, setSyncRefreshKey] = useState(0)
  const [forceSyncBarVisible, setForceSyncBarVisible] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  // Authentication context
  const { user, logout: authLogout, loading: authLoading, isAuthenticated } = useAuth()

  // Concern hooks: FP fetch (abort/dedup/not-found), chrome collapse +
  // CSS vars, the login redirect barrier and the 300ms spinner delay.
  const { franchiseData, loading, fpNotFound, fetchFranchiseData } =
    useFranchiseData(fpCode)

  // Canonicalise the URL to `/inspect/<code>-a` once the FP loads (preserving
  // sub-path/query/hash). Stage 1: the suffix is cosmetic (always `a`), stripped
  // back to the bare code on resolve. `replace` keeps the back button clean; the
  // fetch keys on the bare code, so this never refetches.
  useEffect(() => {
    if (!fpCode || franchiseData?.fp_code !== fpCode) return
    const canonicalRef = buildInspectorRef(fpCode)
    if (fp_code === canonicalRef) return
    const rest = location.pathname.replace(/^\/inspect\/[^/]+/, '')
    navigate(
      `/inspect/${canonicalRef}${rest}${location.search}${location.hash}`,
      { replace: true },
    )
  }, [
    fpCode,
    fp_code,
    franchiseData?.fp_code,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ])
  const { chromeCollapsed } = useAppChromeCollapse()
  const showAuthLoading = useSmartAuthLoading(authLoading)
  useAuthRedirect({ isAuthenticated, authLoading })

  // User menu handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    // Move focus back to the button that opened the menu to prevent aria-hidden issues
    if (userMenuAnchor) {
      (userMenuAnchor as HTMLElement).focus()
    }
    setUserMenuAnchor(null)
  }

  const handleLogout = async () => {
    handleUserMenuClose()
    await authLogout()
  }

  const handleAdminPanel = () => {
    handleUserMenuClose()
    window.open('/admin', '_blank')
  }

  const hasAdminAccess = () => {
    return user && (user.role === 'EVO_ADMIN' || user.role === 'EVO_TEAM')
  }

  const canSwitchFranchise = (() => {
    if (!user) return false
    if (user.role === 'EVO_ADMIN' || user.role === 'EVO_TEAM' || user.role === 'PSC_TEAM') return true
    if (user.role === 'FP_USER') return (user.franchise_access?.length || 0) > 1
    return false
  })()

  // Use centralized navigation configuration, filtered per-user for FP_USER
  // and gated by FP market (e.g. `client` domain hidden for ANZ FPs).
  const navItems = filterNavItemsForUser(
    buildInspectorNavigationItems({ country: franchiseData?.country }),
    user?.role,
  )

  // Shared by the brand bar's Refresh button and the alert stack: re-key
  // the sync bar (forces a fresh poll) and force it visible.
  const handleSyncStarted = () => {
    setSyncRefreshKey(prev => prev + 1)
    setForceSyncBarVisible(true)
  }

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#007f98', mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          ⚡ Checking authentication...
        </Typography>
      </Box>
    )
  }

  // Don't render inspector content if not authenticated
  if (!isAuthenticated) {
    return null
  }

  // Access denied — restricted user trying to access unauthorized FP
  if (user && fpCode && !hasAccessToFp(user, fpCode)) {
    return <FranchiseAccessDenied user={user} fpCode={fpCode} />
  }

  // Franchise partner not found or inaccessible
  if (fpNotFound) {
    return <NotFound />
  }

  return (
    <FeedbackUIProvider>
      <Helmet>
        <meta name="description" content={`Inspector tool for ${franchiseData?.franchise_name || 'franchise partner'}`} />
        <link rel="icon" type="image/svg+xml" href="/favicon-detective.svg" />
        <link rel="alternate icon" href="/favicon-detective.svg" />
        <link rel="apple-touch-icon" href="/favicon-detective.svg" />
      </Helmet>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
      }}>
        {/* Sticky Header - teal brand bar (collapses on scroll). */}
        <InspectorBrandBar
          chromeCollapsed={chromeCollapsed}
          isMobile={isMobile}
          loading={loading}
          franchiseData={franchiseData}
          fpCode={fpCode}
          user={user}
          onMobileNavOpen={() => setMobileNavOpen(true)}
          onSyncStarted={handleSyncStarted}
        />

        {/* Primary Navigation Bar - sticks just below the brand bar.
          * Its `top:` shadows the brand bar's current rendered height
          * via the `--app-chrome-height` CSS variable minus the nav
          * height; transitions in lock-step with the brand bar
          * collapse so the user perceives one merged slide. */}
        {!isMobile && (
          <Box
            className="no-print"
            sx={{
              position: 'sticky',
              top: chromeCollapsed ? 0 : `${APP_HEADER_HEIGHT}px`,
              zIndex: 40,
              backgroundColor: 'white',
              borderBottom: '1px solid #e0e0e0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              transition: 'top 200ms ease',
            }}
          >
            <Container maxWidth="xl">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: APP_NAV_HEIGHT,
                }}
              >
                {/* Navigation Menu */}
                <InspectorNavigation
                  fpCode={fp_code}
                  userRole={user?.role}
                  fpCountry={franchiseData?.country}
                  resources={franchiseData?.resources}
                />

                {/* One flexible spacer: collapse all horizontal slack here so the
                    account cluster docks flush right against the nav tabs. */}
                <Box sx={{ flexGrow: 1 }} />

                {/* User Info */}
                {authLoading && showAuthLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#666' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    >
                      Loading...
                    </Typography>
                  </Stack>
                ) : user ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mr: 1 }}
                  >
                    {/* Account cluster, flush right: divider | bell | user menu */}
                    <Divider
                      orientation="vertical"
                      sx={{ height: 24, borderColor: '#E0E0E0' }}
                    />
                    <NotificationBell placement="inspector" />
                    <IconButton
                      onClick={handleUserMenuOpen}
                      sx={{
                        borderRadius: 2,
                        py: 0.5,
                        px: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          src={!imageError ? getProfileImageUrl(user) : undefined}
                          onError={() => setImageError(true)}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: '#666'
                          }}
                        >
                          {getUserInitials(user)}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#666',
                            fontWeight: 600,
                            fontSize: '0.85rem'
                          }}
                        >
                          {user.name}
                        </Typography>
                        <ExpandMoreIcon sx={{ color: '#666', fontSize: '1rem' }} />
                      </Stack>
                    </IconButton>

                    <Menu
                      anchorEl={userMenuAnchor}
                      open={Boolean(userMenuAnchor)}
                      onClose={handleUserMenuClose}
                      className="no-print"
                      disablePortal={true}
                      disableAutoFocus={true}
                      disableEnforceFocus={true}
                      disableRestoreFocus={false}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem disabled>
                        <Stack spacing={0.5} sx={{ minWidth: 200 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {user.email}
                          </Typography>
                          <Chip
                            label={getRoleDisplayName(user.role)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: getRoleHexColor(user.role),
                              color: 'white',
                              alignSelf: 'flex-start',
                            }}
                          />
                        </Stack>
                      </MenuItem>
                      <Divider />
                      {canSwitchFranchise && (
                        <MenuItem component={Link} to="/choose-franchise" onClick={handleUserMenuClose}>
                          <SwitchIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                          Switch Franchise
                        </MenuItem>
                      )}
                      {hasAdminAccess() && (
                        <MenuItem onClick={handleAdminPanel}>
                          <AdminIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                          Admin Panel
                        </MenuItem>
                      )}
                      <MenuItem onClick={handleLogout}>
                        <LogoutIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                        Logout
                      </MenuItem>
                    </Menu>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1 }}>
                    <WarningIcon sx={{ color: '#f57c00', fontSize: '1.1rem' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#f57c00',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    >
                      NOT AUTHENTICATED
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Container>
          </Box>
        )}

        {/* Sync Status Bar */}
        {fp_code && !loading && franchiseData && (
          <div className="no-print">
            <SyncStatusBarWrapper
              key={syncRefreshKey}
              fpCode={fpCode}
              forceShow={forceSyncBarVisible}
              onActiveChange={setIsSyncing}
              onSyncComplete={() => {
                // Refresh the franchise data after sync completes
                setForceSyncBarVisible(false)
                fetchFranchiseData()
              }}
            />
          </div>
        )}

        {/* Mobile Navigation Drawer - raw ref so nav links keep the tenure
            suffix (`-a`) (matches InspectorNavigation above). */}
        <InspectorMobileNavDrawer
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          fpCode={fp_code}
          navItems={navItems}
          resources={franchiseData?.resources}
          user={user}
          authLoading={authLoading}
          showAuthLoading={showAuthLoading}
          imageError={imageError}
          onImageError={() => setImageError(true)}
          canSwitchFranchise={canSwitchFranchise}
          hasAdminAccess={!!hasAdminAccess()}
          onAdminPanel={handleAdminPanel}
          onLogout={handleLogout}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            backgroundColor: '#f8f9fa',
          }}
        >
          <Container maxWidth="xl" sx={{ py: 3, px: 3 }}>
            <InspectorProvider franchiseData={franchiseData} loading={loading}>
              {fpCode && franchiseData?.fp_code === fpCode && (
                <InspectorAlertsMount
                  fpCode={fpCode}
                  franchiseData={franchiseData}
                  isSyncing={isSyncing}
                  onSyncStarted={handleSyncStarted}
                />
              )}
              <InspectorBreadcrumbs />
              <Outlet />
            </InspectorProvider>
          </Container>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          className="no-print"
          sx={{
            py: 2,
            px: 3,
            backgroundColor: '#f1f3f4',
            borderTop: '1px solid #e0e0e0',
            textAlign: 'center'
          }}
        >
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              For support{' '}
              <a
                href="https://poolwerx.freshdesk.com/a/tickets/new"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007f98', textDecoration: 'underline' }}
              >
                submit a ticket
              </a>
            </Typography>
            <Typography variant="caption" color="text.secondary">•</Typography>
            <VersionDisplay />
          </Stack>
        </Box>

        <DevEnvironmentBanner />
      </Box>

      <HelpFeedbackFab />
      <HelpFeedbackDrawer franchiseData={franchiseData} />
    </FeedbackUIProvider>
  )
}

export default InspectorLayout
